import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Avatar,
    Tooltip,
    Tab,
    Tabs,
    Badge,
    Divider,
    alpha,
    styled
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    EmojiEvents as TrophyIcon,
    Timer as TimerIcon,
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
    GetApp as ExportIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    LocationOn as LocationIcon,
    Group as GroupIcon,
    Stars as StarsIcon,
    HowToReg as ApplyIcon,
    Assignment as ApplicationIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
    }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    '& .MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        fontSize: '0.95rem'
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.light, 0.05),
        transform: 'scale(1.01)'
    }
}));

const CompetitionTracker = () => {
    // Get user info from Redux
    const currentUser = useSelector((state) => state.user?.user);
    const isStudent = currentUser?.role === 'student' || currentUser?.userType === 'student';
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'teacher';

    const [competitions, setCompetitions] = useState([]);
    const [filteredCompetitions, setFilteredCompetitions] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [viewDialog, setViewDialog] = useState(false);
    const [applyDialog, setApplyDialog] = useState(false);
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [applications, setApplications] = useState([]);
    const [applicationForm, setApplicationForm] = useState({
        studentName: '',
        studentId: '',
        class: '',
        section: '',
        rollNo: '',
        email: '',
        phone: '',
        parentName: '',
        parentContact: '',
        reason: '',
        achievements: ''
    });
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        type: '',
        date: '',
        venue: '',
        category: '',
        level: '',
        status: 'upcoming',
        participants: [],
        description: '',
        prizes: '',
        organizer: '',
        registrationDeadline: ''
    });

    // Competition statistics
    const [stats, setStats] = useState({
        total: 0,
        upcoming: 0,
        ongoing: 0,
        completed: 0,
        totalParticipants: 0,
        totalWinners: 0
    });

    useEffect(() => {
        fetchCompetitions();
        fetchApplications();
    }, []);

    useEffect(() => {
        filterCompetitions();
    }, [competitions, searchTerm, filterStatus, selectedTab]);

    const fetchApplications = () => {
        // Mock applications - Replace with actual API call
        const mockApplications = [
            {
                id: 1,
                competitionId: 1,
                studentName: currentUser?.name || 'Current Student',
                studentId: currentUser?.id || 'STD001',
                class: '10',
                section: 'A',
                rollNo: '15',
                email: currentUser?.email || 'student@example.com',
                phone: '9876543210',
                parentName: 'Parent Name',
                parentContact: '9876543211',
                reason: 'Interested in science competitions',
                achievements: 'Won district science fair',
                status: 'pending',
                appliedDate: '2025-11-01'
            }
        ];
        setApplications(mockApplications);
    };

    const fetchCompetitions = () => {
        // Mock data - Replace with actual API call
        const mockCompetitions = [
            {
                id: 1,
                name: 'National Science Olympiad 2025',
                type: 'Olympiad',
                date: '2025-12-15',
                venue: 'National Science Centre, Delhi',
                category: 'Science',
                level: 'National',
                status: 'upcoming',
                participants: [
                    { id: 1, name: 'Rahul Kumar', class: '10-A', rank: null },
                    { id: 2, name: 'Priya Sharma', class: '10-B', rank: null }
                ],
                description: 'Annual national level science olympiad for students',
                prizes: 'Gold, Silver, Bronze medals and certificates',
                organizer: 'Science Olympiad Foundation',
                registrationDeadline: '2025-11-30'
            },
            {
                id: 2,
                name: 'International Mathematics Challenge',
                type: 'Competition',
                date: '2025-11-20',
                venue: 'Online',
                category: 'Mathematics',
                level: 'International',
                status: 'ongoing',
                participants: [
                    { id: 3, name: 'Amit Patel', class: '9-A', rank: null },
                    { id: 4, name: 'Neha Singh', class: '9-C', rank: null },
                    { id: 5, name: 'Arjun Reddy', class: '9-B', rank: null }
                ],
                description: 'International mathematics problem solving competition',
                prizes: 'Trophy and scholarship worth $5000',
                organizer: 'IMO Foundation',
                registrationDeadline: '2025-10-31'
            },
            {
                id: 3,
                name: 'State Level Art Competition',
                type: 'Competition',
                date: '2025-10-10',
                venue: 'State Art Gallery, Mumbai',
                category: 'Arts',
                level: 'State',
                status: 'completed',
                participants: [
                    { id: 6, name: 'Sneha Verma', class: '8-A', rank: 1 },
                    { id: 7, name: 'Rohan Gupta', class: '8-B', rank: 3 }
                ],
                description: 'Annual state level art and painting competition',
                prizes: '1st Prize: ₹10,000, 2nd Prize: ₹5,000, 3rd Prize: ₹3,000',
                organizer: 'State Art Council',
                registrationDeadline: '2025-09-25'
            },
            {
                id: 4,
                name: 'Inter-School Sports Championship',
                type: 'Championship',
                date: '2025-12-01',
                venue: 'City Sports Complex',
                category: 'Sports',
                level: 'District',
                status: 'upcoming',
                participants: [
                    { id: 8, name: 'Vikas Kumar', class: '11-A', rank: null },
                    { id: 9, name: 'Pooja Singh', class: '11-C', rank: null },
                    { id: 10, name: 'Ankit Sharma', class: '10-A', rank: null }
                ],
                description: 'Annual inter-school sports championship',
                prizes: 'Trophy and medals for top 3 positions',
                organizer: 'District Sports Authority',
                registrationDeadline: '2025-11-15'
            }
        ];

        setCompetitions(mockCompetitions);
        calculateStats(mockCompetitions);
    };

    const calculateStats = (data) => {
        const stats = {
            total: data.length,
            upcoming: data.filter(c => c.status === 'upcoming').length,
            ongoing: data.filter(c => c.status === 'ongoing').length,
            completed: data.filter(c => c.status === 'completed').length,
            totalParticipants: data.reduce((sum, c) => sum + c.participants.length, 0),
            totalWinners: data.reduce((sum, c) => sum + c.participants.filter(p => p.rank !== null).length, 0)
        };
        setStats(stats);
    };

    const filterCompetitions = () => {
        let filtered = [...competitions];

        // Filter by tab
        switch (selectedTab) {
            case 1:
                filtered = filtered.filter(c => c.status === 'upcoming');
                break;
            case 2:
                filtered = filtered.filter(c => c.status === 'ongoing');
                break;
            case 3:
                filtered = filtered.filter(c => c.status === 'completed');
                break;
            default:
                break;
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(c =>
                c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.type.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by status dropdown
        if (filterStatus !== 'all') {
            filtered = filtered.filter(c => c.status === filterStatus);
        }

        setFilteredCompetitions(filtered);
    };

    const handleOpenDialog = (competition = null) => {
        if (competition) {
            setFormData(competition);
            setSelectedCompetition(competition);
        } else {
            setFormData({
                id: null,
                name: '',
                type: '',
                date: '',
                venue: '',
                category: '',
                level: '',
                status: 'upcoming',
                participants: [],
                description: '',
                prizes: '',
                organizer: '',
                registrationDeadline: ''
            });
            setSelectedCompetition(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedCompetition(null);
    };

    const handleViewDetails = (competition) => {
        setSelectedCompetition(competition);
        setViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setViewDialog(false);
        setSelectedCompetition(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = () => {
        if (!formData.name || !formData.type || !formData.date || !formData.category) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (formData.id) {
            // Update existing competition
            const updated = competitions.map(c =>
                c.id === formData.id ? formData : c
            );
            setCompetitions(updated);
            calculateStats(updated);
            toast.success('Competition updated successfully');
        } else {
            // Add new competition
            const newCompetition = {
                ...formData,
                id: competitions.length + 1,
                participants: []
            };
            const updated = [...competitions, newCompetition];
            setCompetitions(updated);
            calculateStats(updated);
            toast.success('Competition added successfully');
        }
        handleCloseDialog();
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this competition?')) {
            const updated = competitions.filter(c => c.id !== id);
            setCompetitions(updated);
            calculateStats(updated);
            toast.success('Competition deleted successfully');
        }
    };

    const handleOpenApplyDialog = (competition) => {
        // Pre-fill form with user data if available
        setApplicationForm({
            studentName: currentUser?.name || '',
            studentId: currentUser?.id || '',
            class: currentUser?.class || '',
            section: currentUser?.section || '',
            rollNo: currentUser?.rollNo || '',
            email: currentUser?.email || '',
            phone: currentUser?.phone || '',
            parentName: '',
            parentContact: '',
            reason: '',
            achievements: ''
        });
        setSelectedCompetition(competition);
        setApplyDialog(true);
    };

    const handleCloseApplyDialog = () => {
        setApplyDialog(false);
        setSelectedCompetition(null);
    };

    const handleApplicationInputChange = (e) => {
        const { name, value } = e.target;
        setApplicationForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitApplication = () => {
        if (!applicationForm.studentName || !applicationForm.studentId || !applicationForm.class || !applicationForm.email) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Check if already applied
        const alreadyApplied = applications.some(
            app => app.competitionId === selectedCompetition.id && app.studentId === applicationForm.studentId
        );

        if (alreadyApplied) {
            toast.warning('You have already applied for this competition');
            return;
        }

        // Create new application
        const newApplication = {
            id: applications.length + 1,
            competitionId: selectedCompetition.id,
            ...applicationForm,
            status: 'pending',
            appliedDate: new Date().toISOString().split('T')[0]
        };

        setApplications(prev => [...prev, newApplication]);

        // Add student to competition participants
        const updatedCompetitions = competitions.map(comp => {
            if (comp.id === selectedCompetition.id) {
                return {
                    ...comp,
                    participants: [
                        ...comp.participants,
                        {
                            id: newApplication.id,
                            name: applicationForm.studentName,
                            class: `${applicationForm.class}-${applicationForm.section}`,
                            rank: null
                        }
                    ]
                };
            }
            return comp;
        });

        setCompetitions(updatedCompetitions);
        calculateStats(updatedCompetitions);

        toast.success('Application submitted successfully!');
        handleCloseApplyDialog();
    };

    const hasApplied = (competitionId) => {
        return applications.some(
            app => app.competitionId === competitionId &&
                   app.studentId === (currentUser?.id || applicationForm.studentId)
        );
    };

    const getApplicationStatus = (competitionId) => {
        const app = applications.find(
            app => app.competitionId === competitionId &&
                   app.studentId === (currentUser?.id || applicationForm.studentId)
        );
        return app?.status || null;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'upcoming':
                return 'info';
            case 'ongoing':
                return 'warning';
            case 'completed':
                return 'success';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'upcoming':
                return <PendingIcon />;
            case 'ongoing':
                return <TimerIcon />;
            case 'completed':
                return <CheckCircleIcon />;
            default:
                return null;
        }
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Science': '#4caf50',
            'Mathematics': '#2196f3',
            'Arts': '#e91e63',
            'Sports': '#ff9800',
            'Technology': '#9c27b0'
        };
        return colors[category] || '#757575';
    };

    const statCards = [
        { title: 'Total Competitions', value: stats.total, icon: <TrophyIcon />, color: '#3f51b5' },
        { title: 'Upcoming', value: stats.upcoming, icon: <PendingIcon />, color: '#2196f3' },
        { title: 'Ongoing', value: stats.ongoing, icon: <TimerIcon />, color: '#ff9800' },
        { title: 'Completed', value: stats.completed, icon: <CheckCircleIcon />, color: '#4caf50' },
        { title: 'Total Participants', value: stats.totalParticipants, icon: <GroupIcon />, color: '#9c27b0' },
        { title: 'Winners', value: stats.totalWinners, icon: <StarsIcon />, color: '#f44336' }
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {statCards.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={2} key={index}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <StyledCard
                                sx={{
                                    background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
                                    color: 'white'
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                                {stat.title}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                                            {stat.icon}
                                        </Avatar>
                                    </Box>
                                </CardContent>
                            </StyledCard>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>

            {/* Filters and Actions */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search competitions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Status</InputLabel>
                            <Select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                label="Filter by Status"
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="upcoming">Upcoming</MenuItem>
                                <MenuItem value="ongoing">Ongoing</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ textAlign: 'right' }}>
                        <Button
                            variant="outlined"
                            startIcon={<ExportIcon />}
                            sx={{ mr: 2 }}
                        >
                            Export
                        </Button>
                        {!isStudent && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                            >
                                Add Competition
                            </Button>
                        )}
                        {isStudent && (
                            <Chip
                                icon={<ApplicationIcon />}
                                label={`My Applications: ${applications.filter(app => app.studentId === currentUser?.id).length}`}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={selectedTab}
                    onChange={(e, newValue) => setSelectedTab(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={`All (${stats.total})`} />
                    <Tab label={`Upcoming (${stats.upcoming})`} />
                    <Tab label={`Ongoing (${stats.ongoing})`} />
                    <Tab label={`Completed (${stats.completed})`} />
                </Tabs>
            </Paper>

            {/* Competition List */}
            <StyledTableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Competition Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Level</TableCell>
                            <TableCell>Participants</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <AnimatePresence>
                            {filteredCompetitions.length > 0 ? (
                                filteredCompetitions.map((competition, index) => (
                                    <StyledTableRow
                                        key={competition.id}
                                        component={motion.tr}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: getCategoryColor(competition.category),
                                                        mr: 2,
                                                        width: 40,
                                                        height: 40
                                                    }}
                                                >
                                                    <TrophyIcon />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {competition.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {competition.organizer}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={competition.type}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={competition.category}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(getCategoryColor(competition.category), 0.1),
                                                    color: getCategoryColor(competition.category),
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CalendarIcon sx={{ mr: 1, fontSize: 18, color: 'action.active' }} />
                                                <Typography variant="body2">
                                                    {new Date(competition.date).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={competition.level}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                badgeContent={competition.participants.length}
                                                color="primary"
                                                sx={{ mr: 2 }}
                                            >
                                                <GroupIcon />
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(competition.status)}
                                                label={competition.status}
                                                color={getStatusColor(competition.status)}
                                                size="small"
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => handleViewDetails(competition)}
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {isStudent ? (
                                                // Student actions
                                                <>
                                                    {hasApplied(competition.id) ? (
                                                        <Chip
                                                            label={getApplicationStatus(competition.id) === 'approved' ? 'Registered' : 'Applied'}
                                                            color={getApplicationStatus(competition.id) === 'approved' ? 'success' : 'warning'}
                                                            size="small"
                                                            icon={<CheckCircleIcon />}
                                                        />
                                                    ) : competition.status === 'upcoming' &&
                                                       new Date(competition.registrationDeadline) > new Date() ? (
                                                        <Tooltip title="Apply for Competition">
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<ApplyIcon />}
                                                                onClick={() => handleOpenApplyDialog(competition)}
                                                            >
                                                                Apply
                                                            </Button>
                                                        </Tooltip>
                                                    ) : (
                                                        <Chip
                                                            label="Registration Closed"
                                                            size="small"
                                                            color="default"
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                // Admin/Teacher actions
                                                <>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleOpenDialog(competition)}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDelete(competition.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </TableCell>
                                    </StyledTableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Box sx={{ py: 4 }}>
                                            <TrophyIcon sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
                                            <Typography variant="h6" color="textSecondary">
                                                No competitions found
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </AnimatePresence>
                    </TableBody>
                </Table>
            </StyledTableContainer>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {selectedCompetition ? 'Edit Competition' : 'Add New Competition'}
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Competition Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    label="Type"
                                >
                                    <MenuItem value="Olympiad">Olympiad</MenuItem>
                                    <MenuItem value="Competition">Competition</MenuItem>
                                    <MenuItem value="Championship">Championship</MenuItem>
                                    <MenuItem value="Hackathon">Hackathon</MenuItem>
                                    <MenuItem value="Quiz">Quiz</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    label="Category"
                                >
                                    <MenuItem value="Science">Science</MenuItem>
                                    <MenuItem value="Mathematics">Mathematics</MenuItem>
                                    <MenuItem value="Arts">Arts</MenuItem>
                                    <MenuItem value="Sports">Sports</MenuItem>
                                    <MenuItem value="Technology">Technology</MenuItem>
                                    <MenuItem value="Literature">Literature</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Level</InputLabel>
                                <Select
                                    name="level"
                                    value={formData.level}
                                    onChange={handleInputChange}
                                    label="Level"
                                >
                                    <MenuItem value="School">School</MenuItem>
                                    <MenuItem value="District">District</MenuItem>
                                    <MenuItem value="State">State</MenuItem>
                                    <MenuItem value="National">National</MenuItem>
                                    <MenuItem value="International">International</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Date"
                                name="date"
                                type="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Registration Deadline"
                                name="registrationDeadline"
                                type="date"
                                value={formData.registrationDeadline}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Venue"
                                name="venue"
                                value={formData.venue}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    label="Status"
                                >
                                    <MenuItem value="upcoming">Upcoming</MenuItem>
                                    <MenuItem value="ongoing">Ongoing</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Organizer"
                                name="organizer"
                                value={formData.organizer}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Prizes"
                                name="prizes"
                                value={formData.prizes}
                                onChange={handleInputChange}
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained">
                        {selectedCompetition ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog
                open={viewDialog}
                onClose={handleCloseViewDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                {selectedCompetition && (
                    <>
                        <DialogTitle sx={{ pb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                        sx={{
                                            bgcolor: getCategoryColor(selectedCompetition.category),
                                            mr: 2,
                                            width: 56,
                                            height: 56
                                        }}
                                    >
                                        <TrophyIcon fontSize="large" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                            {selectedCompetition.name}
                                        </Typography>
                                        <Chip
                                            icon={getStatusIcon(selectedCompetition.status)}
                                            label={selectedCompetition.status}
                                            color={getStatusColor(selectedCompetition.status)}
                                            size="small"
                                            sx={{ textTransform: 'capitalize', mt: 1 }}
                                        />
                                    </Box>
                                </Box>
                                <IconButton onClick={handleCloseViewDialog}>
                                    <CancelIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <Divider />
                        <DialogContent sx={{ pt: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                            Type & Category
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Chip label={selectedCompetition.type} variant="outlined" />
                                            <Chip
                                                label={selectedCompetition.category}
                                                sx={{
                                                    bgcolor: alpha(getCategoryColor(selectedCompetition.category), 0.1),
                                                    color: getCategoryColor(selectedCompetition.category),
                                                    fontWeight: 600
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                            Level
                                        </Typography>
                                        <Chip label={selectedCompetition.level} color="primary" />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />
                                        <Box>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Competition Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {new Date(selectedCompetition.date).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <LocationIcon sx={{ mr: 1, color: 'action.active' }} />
                                        <Box>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Venue
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {selectedCompetition.venue}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                            Organizer
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {selectedCompetition.organizer}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                            Description
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedCompetition.description}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                            Prizes
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedCompetition.prizes}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Participants ({selectedCompetition.participants.length})
                                    </Typography>
                                    {selectedCompetition.participants.length > 0 ? (
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Class</TableCell>
                                                        <TableCell>Rank</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedCompetition.participants.map((participant) => (
                                                        <TableRow key={participant.id}>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                                                                        {participant.name.charAt(0)}
                                                                    </Avatar>
                                                                    {participant.name}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>{participant.class}</TableCell>
                                                            <TableCell>
                                                                {participant.rank ? (
                                                                    <Chip
                                                                        label={`Rank ${participant.rank}`}
                                                                        color="success"
                                                                        size="small"
                                                                        icon={<TrophyIcon />}
                                                                    />
                                                                ) : (
                                                                    <Chip label="Participating" size="small" variant="outlined" />
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            No participants registered yet
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={handleCloseViewDialog} variant="outlined">
                                Close
                            </Button>
                            <Button
                                onClick={() => {
                                    handleCloseViewDialog();
                                    handleOpenDialog(selectedCompetition);
                                }}
                                variant="contained"
                                startIcon={<EditIcon />}
                            >
                                Edit
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Student Application Dialog */}
            <Dialog
                open={applyDialog}
                onClose={handleCloseApplyDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                {selectedCompetition && (
                    <>
                        <DialogTitle sx={{ pb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    sx={{
                                        bgcolor: getCategoryColor(selectedCompetition.category),
                                        mr: 2,
                                        width: 56,
                                        height: 56
                                    }}
                                >
                                    <ApplyIcon fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        Apply for Competition
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {selectedCompetition.name}
                                    </Typography>
                                </Box>
                            </Box>
                        </DialogTitle>
                        <Divider />
                        <DialogContent sx={{ pt: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        Student Information
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Student Name"
                                        name="studentName"
                                        value={applicationForm.studentName}
                                        onChange={handleApplicationInputChange}
                                        InputProps={{
                                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Student ID"
                                        name="studentId"
                                        value={applicationForm.studentId}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Class"
                                        name="class"
                                        value={applicationForm.class}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Section"
                                        name="section"
                                        value={applicationForm.section}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Roll No"
                                        name="rollNo"
                                        value={applicationForm.rollNo}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={applicationForm.email}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={applicationForm.phone}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>

                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        Parent/Guardian Information
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Parent Name"
                                        name="parentName"
                                        value={applicationForm.parentName}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Parent Contact"
                                        name="parentContact"
                                        value={applicationForm.parentContact}
                                        onChange={handleApplicationInputChange}
                                    />
                                </Grid>

                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        Additional Information
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Why do you want to participate?"
                                        name="reason"
                                        value={applicationForm.reason}
                                        onChange={handleApplicationInputChange}
                                        placeholder="Tell us why you're interested in this competition..."
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Relevant Achievements"
                                        name="achievements"
                                        value={applicationForm.achievements}
                                        onChange={handleApplicationInputChange}
                                        placeholder="List any relevant achievements, awards, or experiences..."
                                    />
                                </Grid>

                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Paper sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Competition Details:</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            • Date: {new Date(selectedCompetition.date).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Venue: {selectedCompetition.venue}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Registration Deadline: {new Date(selectedCompetition.registrationDeadline).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Level: {selectedCompetition.level}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={handleCloseApplyDialog} variant="outlined">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitApplication}
                                variant="contained"
                                color="success"
                                startIcon={<ApplyIcon />}
                            >
                                Submit Application
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default CompetitionTracker;